import type { Ref } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';

import { Close } from '@rover/icons';
import NoScrollController from '@rover/kibble/patterns/Modal/utils/NoScrollController';
import { MQ, ZIndex } from '@rover/kibble/styles';
import Button, { ButtonType } from '@rover/react-lib/components/buttons/Button';
import useStaticId from '@rover/react-lib/hooks/useStaticId';

import { defaultProps as commonDefaultProps } from './Modal.common';
import type { Props as CommonProps } from './Modal.types';
import StyledModal from './ModalStylingAdapter';
import SearchPerfHackButton from './SearchPerfHackButton';

export const StyledButton = styled(Button)`
  display: flex;
  padding: 0;
  position: absolute;
  right: 14px;
  top: 14px;
  z-index: ${ZIndex.MODAL.toString()};

  ${MQ.NOT_XS.toString()} {
    right: 35px;
    top: 35px;
  }
`;
export const StyledClose = styled(Close)`
  cursor: pointer;
  fill: rgba(0, 0, 0, 0.2);
  height: 16px;
  width: 16px;

  &:hover {
    fill: rgba(0, 0, 0, 0.5);
  }

  ${MQ.NOT_XS.toString()} {
    height: 24px;
    width: 24px;
  }
`;
export type Props = CommonProps & {
  aria?: Record<string, any>;
  appElementSelector?: string;
  'aria-labelledby'?: string;
  closeTimeout?: number;
  contentRef?: Ref<any> | null | undefined;
  data?: Record<string, string>;
  disablePortal?: boolean;
  hasCloseButton?: boolean;
  overlayRef?: Ref<any> | null | undefined;
  style?: {
    content?: Record<string, string | number>;
    overlay?: Record<string, string | number>;
  };
  useSearchPerfHack?: boolean;
  className?: string;
};
const noScroll = new NoScrollController();

const Modal = ({
  appElementSelector,
  children,
  closeTimeout,
  disablePortal,
  hasCloseButton,
  isOpen,
  onRequestClose,
  style,
  useSearchPerfHack,
  ...other
}: Props): JSX.Element => {
  // setAppElement has to be called before render or else we get react-modal errors.
  // useMemo with an empty array second arg will run only once, before render, much like componentWillMount does.
  // react-modal's internals make this method safe to call server-side.
  useMemo(() => {
    ReactModal.setAppElement(
      appElementSelector !== undefined ? appElementSelector : Modal.defaultProps.appElementSelector
    ); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const modalId = useStaticId();
  useEffect(() => {
    if (isOpen) {
      noScroll.on(modalId);
    } else {
      noScroll.off(modalId);
    }

    return () => {
      noScroll.off(modalId);
    };
  }, [isOpen, modalId]);
  // After mount, switch back to the regular button.  This allows onRequestClose to fire as
  // expected once we're out of the mounting/rehyrdrating period during which the hack helps
  const [showPerfHackButton, setShowPerfHackButton] = useState(useSearchPerfHack);
  useEffect(() => setShowPerfHackButton(false), []);
  // ReactModal and ServerModal both take all aria-* props in an `aria` object.
  const ariaProps = other.aria || {};
  Object.keys(other).forEach((key) => {
    if (key.startsWith('aria-')) {
      ariaProps[key.substr(5)] = other[key];
      delete other[key]; // eslint-disable-line no-param-reassign
    }
  });
  return (
    <StyledModal
      {...other}
      closeTimeoutMS={closeTimeout !== undefined ? closeTimeout : Modal.defaultProps.closeTimeout}
      disablePortal={disablePortal !== undefined ? disablePortal : Modal.defaultProps.disablePortal}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      aria={ariaProps}
      style={style}
    >
      {showPerfHackButton ? <SearchPerfHackButton /> : null}
      {hasCloseButton && !showPerfHackButton ? (
        <StyledButton
          aria-label="Close Modal"
          buttonType={ButtonType.TEXT}
          onClick={onRequestClose}
          data-qa-id="modal-button-close"
          role="button"
        >
          <StyledClose />
        </StyledButton>
      ) : null}
      {children}
    </StyledModal>
  );
};

Modal.defaultProps = {
  ...commonDefaultProps,
  appElementSelector: '#base-content',
  closeTimeout: 150,
  disablePortal: false,
  hasCloseButton: true,
  style: undefined,
  useSearchPerfHack: false,
};
export default Modal;
